// ============================================================================
// OBT F3-C — Rapor türetmeleri (SAF fonksiyonlar; sahte metrik YOK)
// ============================================================================
// Bu dosya YALNIZCA backend'in gerçekten döndürdüğü alanlardan oran/özet türetir.
// Backend'in vermediği bir metrik (ör. bölge bazlı başarı, kampanya-üstü personel
// toplamı) BURADA UYDURULMAZ — ekranlar bu boşlukları dürüstçe belirtir.
// ============================================================================

/** Yayın akışında "devam eden" sayılan kampanya durumları. */
export const ONGOING_CAMPAIGN_STATUSES = Object.freeze([
  'published',
  'collecting_responses',
  'under_review',
  'director_review',
]);

/** Tamamlanmış/karara bağlanmış sayılan durumlar. */
export const COMPLETED_CAMPAIGN_STATUSES = Object.freeze(['decided', 'completed']);

/** Cevap oranı: gönderen kişi / hedef kişi. Hedef 0 ise null (tanımsız). */
export function responseRate(campaign) {
  const target = Number(campaign?.target_count || 0);
  if (target <= 0) return null;
  const noResponse = Number(campaign?.no_response_person_count || 0);
  const responded = Math.max(0, target - noResponse);
  return responded / target;
}

/** Bir kampanya listesinden toplu (kişi-ağırlıklı) cevap oranı. */
export function aggregateResponseRate(campaigns) {
  const list = Array.isArray(campaigns) ? campaigns : [];
  let target = 0;
  let responded = 0;
  for (const c of list) {
    const t = Number(c?.target_count || 0);
    if (t <= 0) continue;
    target += t;
    responded += Math.max(0, t - Number(c?.no_response_person_count || 0));
  }
  if (target <= 0) return null;
  return responded / target;
}

/**
 * Kampanya durumlarına göre sayım. `statusTotals` verilmişse (durum→toplam,
 * sunucu `page.total`'larından) onu kullanır; yoksa mevcut kampanya
 * listesinden gruplar (yalnız yüklenen sayfayı kapsar — bu SINIRI çağıran
 * ekran belirtir).
 */
export function campaignStatusCounts(campaigns) {
  const counts = {};
  for (const c of Array.isArray(campaigns) ? campaigns : []) {
    const s = c?.status || 'unknown';
    counts[s] = (counts[s] || 0) + 1;
  }
  return counts;
}

/** Devam eden kampanya sayısı (durum→sayım haritasından). */
export function sumByStatuses(counts, statuses) {
  const map = counts || {};
  return statuses.reduce((acc, s) => acc + (Number(map[s]) || 0), 0);
}

/** Kampanyanın "süresi geçmiş kişi" içerip içermediği. */
export function hasOverdue(campaign) {
  return Number(campaign?.late_person_count || 0) > 0;
}

/**
 * response-summary.by_review_state'ten revizyon (BM/MY revizyon isteği)
 * dağılımını ayıklar. Backend revizyon-oranı alanı VERMEZ; bu, revizyon
 * durumlarının SAYIMIDIR (oran değil).
 */
export const REVISION_REVIEW_STATES = Object.freeze([
  'bm_revision_requested',
  'my_revision_requested',
]);

export function revisionCountFromSummary(summary) {
  const by = summary?.by_review_state || {};
  return REVISION_REVIEW_STATES.reduce((acc, s) => acc + (Number(by[s]) || 0), 0);
}

/** Reddedilen cevap sayısı (BM/Müdür reddi) — response-summary'den. */
export const REJECTED_REVIEW_STATES = Object.freeze(['bm_rejected', 'director_rejected']);

export function rejectedCountFromSummary(summary) {
  const by = summary?.by_review_state || {};
  return REJECTED_REVIEW_STATES.reduce((acc, s) => acc + (Number(by[s]) || 0), 0);
}

/** by_scope_source['approved_suggestion'] → onaylı öneri (suggested) cevap sayısı. */
export function suggestedCountFromSummary(summary) {
  const by = summary?.by_scope_source || {};
  return Number(by.approved_suggestion || 0);
}

/** Bir duration stage'ini isimden bul. */
export function findStage(durations, stageName) {
  const stages = durations?.stages || [];
  return stages.find((s) => s?.stage === stageName) || null;
}

/** "Ortalama cevap süresi" = published_to_response.average_seconds (saniye|null). */
export function averageResponseSeconds(durations) {
  const stage = findStage(durations, 'published_to_response');
  const v = stage?.average_seconds;
  return typeof v === 'number' ? v : null;
}

// --- Park geçmişi türetmeleri (ParkObtHistoryOut.entries'ten) ----------------

/** Park için özet sayılar — YALNIZ entries'ten türetilir (backend scalar vermez). */
export function summarizeParkHistory(history) {
  const entries = Array.isArray(history?.entries) ? history.entries : [];
  let responseCount = 0;
  let revisionCount = 0;
  let photoCount = 0;
  let lastSubmittedAt = null;
  const campaignIds = new Set();
  let lastCampaignTitle = null;

  for (const e of entries) {
    if (e?.response_id) responseCount += 1;
    revisionCount += Array.isArray(e?.revisions) ? e.revisions.length : 0;
    photoCount += Array.isArray(e?.attachments) ? e.attachments.length : 0;
    if (e?.campaign_id) campaignIds.add(String(e.campaign_id));
    const ts = e?.submitted_at ? Date.parse(e.submitted_at) : NaN;
    if (!Number.isNaN(ts) && (lastSubmittedAt === null || ts > lastSubmittedAt)) {
      lastSubmittedAt = ts;
      lastCampaignTitle = e?.campaign_title || null;
    }
  }
  // entries backend'de en yeni→eski sıralı; ilk giriş "son kampanya" bağlamıdır.
  if (lastCampaignTitle === null && entries.length > 0) {
    lastCampaignTitle = entries[0]?.campaign_title || null;
  }
  return {
    campaignCount: campaignIds.size,
    responseCount,
    revisionCount,
    photoCount,
    lastSubmittedAt: lastSubmittedAt ? new Date(lastSubmittedAt).toISOString() : null,
    lastCampaignTitle,
  };
}

/** Respondent satırından "bekleyen park" (atanan - cevaplanan, ≥0). */
export function pendingParks(row) {
  const responsible = Number(row?.responsible_park_count || 0);
  const answered = Number(row?.answered_park_count || 0);
  return Math.max(0, responsible - answered);
}
